import { useQuery, useQueryClient } from 'react-query';

import type { MessageContent } from '@captivateiq/assistant/types';

import { getRequest, postRequest } from 'services/apiRequests';
import type { APIList, StringifiedUUID } from 'types';
import type { APIError } from 'utils/errors';

export const INSIGHT_STATUS_SURFACED = 'surfaced';

export type PlanningCycleInsight = {
  object: 'planning_cycle_insight';
  id: StringifiedUUID;
  run_uuid: StringifiedUUID;
  category: string;
  headline: string | null;
  body: string | null;
  discuss_prompt: string | null;
  status: string;
  impact_score: number | null;
  scenario_id: StringifiedUUID;
  result_scenario_id: StringifiedUUID | null;
  promoted_at: string | null;
  generated_at: string;
  chart_blocks?: MessageContent[] | null;
};

export type TriggerInsightsResponse = {
  aggregation_id: StringifiedUUID;
};

export type ActiveRunResponse = {
  aggregation_id: StringifiedUUID | null;
};

const getSPMPlanningCycleInsights = (planningCycleId: StringifiedUUID, status: string = INSIGHT_STATUS_SURFACED) => {
  return getRequest<{ status: string }, APIList<PlanningCycleInsight>>(
    `/api/v1/tq/planning-cycles/${planningCycleId}/insights/`,
    { status },
  );
};

const useGetSPMPlanningCycleInsightsQuery = (planningCycleId: StringifiedUUID) => {
  const QUERY_KEY = ['spm-planning-cycle-insights', planningCycleId];
  const queryClient = useQueryClient();

  const query = useQuery<APIList<PlanningCycleInsight>, APIError>(
    QUERY_KEY,
    () => getSPMPlanningCycleInsights(planningCycleId),
    { enabled: !!planningCycleId },
  );

  const invalidateQuery = () => queryClient.invalidateQueries(QUERY_KEY);

  return { query, invalidateQuery };
};

const triggerSPMPlanningCycleInsights = (
  csrfToken: string,
  planningCycleId: StringifiedUUID,
  data: { scenario_uuid: StringifiedUUID; hypothesis_count?: number; top_n?: number },
) => {
  return postRequest<typeof data, TriggerInsightsResponse>(
    `/api/v1/tq/planning-cycles/${planningCycleId}/insights/trigger/`,
    data,
    csrfToken,
  );
};

const getSPMPlanningCycleInsightsActiveRun = (planningCycleId: StringifiedUUID) => {
  return getRequest<null, ActiveRunResponse>(`/api/v1/tq/planning-cycles/${planningCycleId}/insights/active-run/`);
};

const promoteSPMPlanningCycleInsightScenario = (
  csrfToken: string,
  planningCycleId: StringifiedUUID,
  insightId: StringifiedUUID,
) => {
  return postRequest<null, PlanningCycleInsight>(
    `/api/v1/tq/planning-cycles/${planningCycleId}/insights/${insightId}/promote-scenario/`,
    null,
    csrfToken,
  );
};

export const SPMPlanningCycleInsightsService = {
  getSPMPlanningCycleInsights,
  useGetSPMPlanningCycleInsightsQuery,
  triggerSPMPlanningCycleInsights,
  getSPMPlanningCycleInsightsActiveRun,
  promoteSPMPlanningCycleInsightScenario,
};
